'use client'

import { useMemo, useState } from 'react'

type HeatmapProps = {
  activityDates: Date[]
}

export default function ContributionHeatmap({ activityDates }: HeatmapProps) {
  const [tooltip, setTooltip] = useState<{ x: number, y: number, date: string, count: number } | null>(null)

  // Calculate the grid data
  const { weeks, maxCount } = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    // Create a lookup map of 'YYYY-MM-DD' -> count
    const activityMap = new Map<string, number>()
    activityDates.forEach(date => {
      const dateString = new Date(date).toISOString().split('T')[0]
      activityMap.set(dateString, (activityMap.get(dateString) || 0) + 1)
    })

    const weeksArray: { date: Date; count: number }[][] = []
    let currentWeek: { date: Date; count: number }[] = []
    
    // Generate exactly 365 days (52 weeks) going backwards
    for (let i = 364; i >= 0; i--) {
      const d = new Date(today)
      d.setDate(today.getDate() - i)
      const dateString = d.toISOString().split('T')[0]
      const count = activityMap.get(dateString) || 0
      
      currentWeek.push({ date: d, count })
      
      // If it's Saturday (6) or the last day, push the week
      if (d.getDay() === 6 || i === 0) {
        weeksArray.push(currentWeek)
        currentWeek = []
      }
    }

    // Find max activity for color scaling
    const max = Math.max(...Array.from(activityMap.values()), 1)
    return { weeks: weeksArray, maxCount: max }
  }, [activityDates])

  const getColor = (count: number) => {
    if (count === 0) return '#0f172a' // slate-900
    if (count <= 2) return '#064e3b'  // emerald-900
    if (count <= 5) return '#047857'  // emerald-600
    if (count <= 8) return '#10b981'  // emerald-500
    return '#34d399'                  // emerald-400 (max intensity)
  }

  const CELL_SIZE = 12
  const CELL_GAP = 3

  return (
    <div className="w-full relative flex flex-col items-center sm:items-start group">
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <span className="text-xs font-mono font-bold text-slate-400 uppercase tracking-widest">
          Contribution Matrix (365d)
        </span>
      </div>

      <div className="overflow-x-auto w-full pb-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        <svg 
          width={weeks.length * (CELL_SIZE + CELL_GAP)} 
          height={7 * (CELL_SIZE + CELL_GAP)} 
          className="min-w-max"
          onMouseLeave={() => setTooltip(null)}
        >
          {weeks.map((week, weekIndex) => (
            <g key={weekIndex} transform={`translate(${weekIndex * (CELL_SIZE + CELL_GAP)}, 0)`}>
              {week.map((day, dayIndex) => {
                // Offset days so Sunday aligns with 0, Monday with 1, etc.
                const yOffset = day.date.getDay() * (CELL_SIZE + CELL_GAP)
                
                return (
                  <rect
                    key={dayIndex}
                    y={yOffset}
                    width={CELL_SIZE}
                    height={CELL_SIZE}
                    fill={getColor(day.count)}
                    rx={2} // Sharp, squared-off corners (rounded-sm style)
                    className="transition-colors duration-200 hover:stroke-slate-100 hover:stroke-1 cursor-crosshair"
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect()
                      setTooltip({
                        x: rect.left + window.scrollX,
                        y: rect.top + window.scrollY - 30, // Position above the square
                        date: day.date.toDateString(),
                        count: day.count
                      })
                    }}
                  />
                )
              })}
            </g>
          ))}
        </svg>
      </div>

      {/* Dynamic Tooltip */}
      {tooltip && (
        <div 
          className="absolute z-50 pointer-events-none bg-slate-900 border border-slate-700 text-slate-100 text-[10px] font-mono px-2 py-1 rounded-sm shadow-xl whitespace-nowrap"
          style={{ top: tooltip.y, left: tooltip.x, transform: 'translateX(-50%)' }}
        >
          <span className="font-bold text-emerald-400">{tooltip.count}</span> contributions on {tooltip.date}
        </div>
      )}
    </div>
  )
}